/** @jsx jsx */

import { ChangeEvent, FormEvent, Fragment, useState } from 'react';

import { Checkbox, Field, TextInput } from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import AsyncButton from 'app/developer-v3/components/AsyncButton';
import Link from 'app/common/components/Link';

import Description from '../Description';
import { DropdownAndTagsContainer } from './DropdownAndTagsContainer';
import { FormatItems } from './TriggerActionAndOthersDropdown';

const stylesForm = css`
  display: grid;
  gap: 24px;
  justify-items: flex-start;

  & > [class*='Field'] {
    width: 100% !important;
  }
`;

enum Reason {
  hasNewFeatures = 'hasNewFeatures',
  hasBugFixes = 'hasBugFixes',
  hasOtherChanges = 'hasOtherChanges',
}

type ChangeType = 'BUGFIX' | 'FEATURE_UPDATE';

export type AppMetadata = {
  appChangeType: ChangeType;
  actionKey: string;
  actionType: 'read' | 'search' | 'write';
};

export type IssueMetadata = {
  appChangeType: ChangeType;
  issueId: number;
};

export type Options = {
  appMetadata: AppMetadata[];
  changelog?: string;
  isBugFix: boolean;
  isFeatureUpdate: boolean;
  isOther: boolean;
  issueMetadata: IssueMetadata[];
};

type Props = {
  initialChangelog?: string;
  isLoading: boolean;
  onSubmit: (version: string, options: Options) => void;
  version: string;
};

function Start({ initialChangelog, isLoading, onSubmit, version }: Props) {
  const [reasons, setReasons] = useState({
    hasNewFeatures: false,
    hasBugFixes: false,
    hasOtherChanges: false,
  });
  const [bugfixMetadata, setBugfixMetadata] = useState<FormatItems[]>([]);
  const [featureUpdateMetadata, setFeatureUpdateMetadata] = useState<
    FormatItems[]
  >([]);
  const [changelog, setChangelog] = useState(initialChangelog);

  const transformAppMetadataItems = (
    item: FormatItems,
    changeType: ChangeType
  ): AppMetadata => ({
    appChangeType: changeType,
    actionKey: item.key,
    actionType: item.type,
  });

  const transformIssueMetadataItems = (
    item: FormatItems,
    changeType: ChangeType
  ): IssueMetadata => ({
    appChangeType: changeType,
    issueId: item.issue,
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const appMetadata = [
      ...bugfixMetadata
        .filter(item => item.key && item.type)
        .map(item => transformAppMetadataItems(item, 'BUGFIX')),
      ...featureUpdateMetadata
        .filter(item => item.key && item.type)
        .map(item => transformAppMetadataItems(item, 'FEATURE_UPDATE')),
    ];

    const issueMetadata = [
      ...bugfixMetadata
        .filter(item => item.issue)
        .map(item => transformIssueMetadataItems(item, 'BUGFIX')),
      ...featureUpdateMetadata
        .filter(item => item.issue)
        .map(item => transformIssueMetadataItems(item, 'FEATURE_UPDATE')),
    ];

    const options: Options = {
      appMetadata,
      changelog,
      isBugFix: reasons.hasBugFixes,
      isFeatureUpdate: reasons.hasNewFeatures,
      isOther: reasons.hasOtherChanges,
      issueMetadata,
    };

    onSubmit(version, options);
  };

  const handleSelection = (
    item: FormatItems,
    metadataType: 'bug' | 'feature'
  ) => {
    if (!item) return;

    if (metadataType === 'bug') {
      setBugfixMetadata([item, ...bugfixMetadata]);
    } else {
      setFeatureUpdateMetadata([item, ...featureUpdateMetadata]);
    }
  };

  const setReason = (reason: Reason, isChecked: boolean) => {
    setReasons(prevReasons => ({ ...prevReasons, [reason]: isChecked }));
  };

  // When the feature flag is enabled, we want to disable the promote button
  // if there are no reasons selected or if the description is empty.
  // (Old logic) When the feature flag is disabled, we want to disable
  // the promote button if there are no reasons selected
  const isPromoteButtonDisabled =
    !Object.values(reasons).some(value => value) || !changelog?.trim();

  return (
    <Fragment>
      <Description>
        When you make a version public in your integration, it becomes the
        default for users creating Zaps. The earlier public version turns
        private but remains usable in the Zap editor. Zap templates using your
        integration will update to the new version unless there are significant
        changes. If there are, they'll stay on the current version.{' '}
        <Link
          href="https://docs.zapier.com/platform/manage/promote"
          target="_blank"
        >
          Learn more
        </Link>
        .
      </Description>
      <Description>
        Please provide the Zapier team and your end users with the reason for
        your integration updates.
      </Description>
      <form css={stylesForm} noValidate={true} onSubmit={handleSubmit}>
        <Field
          helpTextLineClamp={3}
          isRequired={true}
          label="What changed?"
          renderHelpText={() => (
            <p>
              Relating features or bugs to the promotion will queue them to be
              reviewed for closure by the Zapier Support team.{' '}
              <Link
                href="https://docs.zapier.com/platform/manage/user-feedback#3-close-resolved-issues"
                target="_blank"
              >
                Learn more
              </Link>
              .
            </p>
          )}
          renderInput={() => (
            <Fragment>
              <Checkbox
                isSelected={reasons.hasNewFeatures}
                value={Reason.hasNewFeatures}
                name="checkbox"
                onChange={(isSelected: boolean) => {
                  setReason(Reason.hasNewFeatures, isSelected);
                }}
              >
                New Features
              </Checkbox>
              {reasons.hasNewFeatures && (
                <DropdownAndTagsContainer
                  label="Related trigger / action / feature request"
                  onRemoveTag={tag =>
                    setFeatureUpdateMetadata(
                      featureUpdateMetadata.filter(item => item.value !== tag)
                    )
                  }
                  onSelect={(item: FormatItems) =>
                    handleSelection(item, 'feature')
                  }
                  selectedItems={featureUpdateMetadata}
                />
              )}
              <Checkbox
                isSelected={reasons.hasBugFixes}
                value={Reason.hasBugFixes}
                name="checkbox"
                onChange={(isSelected: boolean) => {
                  setReason(Reason.hasBugFixes, isSelected);
                }}
              >
                Bug Fixes
              </Checkbox>
              {reasons.hasBugFixes && (
                <DropdownAndTagsContainer
                  label="Related trigger / action / bug"
                  onRemoveTag={tag =>
                    setBugfixMetadata(
                      bugfixMetadata.filter(item => item.value !== tag)
                    )
                  }
                  onSelect={(item: FormatItems) => handleSelection(item, 'bug')}
                  selectedItems={bugfixMetadata}
                />
              )}
              <Checkbox
                isSelected={reasons.hasOtherChanges}
                value={Reason.hasOtherChanges}
                name="checkbox"
                onChange={(isSelected: boolean) => {
                  setReason(Reason.hasOtherChanges, isSelected);
                }}
              >
                Other
              </Checkbox>
            </Fragment>
          )}
        />
        <Field
          helpTextLineClamp={2}
          isRequired={true}
          label="User-facing description"
          renderHelpText={() => (
            <p>
              The clearer your description, the more likely Zapier’s marketing
              team is to highlight your update! (max 255 characters)
            </p>
          )}
          renderInput={descriptionProps => (
            <TextInput
              aria-label="User-facing description"
              placeholder={changelog || 'How does this update help your users?'}
              {...descriptionProps}
              isMultiline={true}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                setChangelog(event.currentTarget.value);
              }}
              rows={5}
              value={changelog}
            />
          )}
        />
        <AsyncButton
          aria-label={`Promote version ${version}`}
          variant="primary"
          isDisabled={isPromoteButtonDisabled}
          isWorking={isLoading}
          type="submit"
        >
          Promote
        </AsyncButton>
      </form>
    </Fragment>
  );
}

export default Start;
